/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Injectable, NgZone, inject } from '@angular/core';
import { ApiError, parseApiError } from '@app/shared/models/api/api-error.model';
import { applyTransaction, logAction } from '@datorama/akita';
import {
  Observable,
  Subscription,
  throwError,
  catchError,
  tap,
  of,
  switchMap,
} from 'rxjs';
import { AkitaCheckoutStore } from './checkout.store';
import { SessionModel } from '../models/session.model';
import { SessionApiService } from '../services/checkout-session.service';
import { AkitaCartQuery } from '@app/akita/cart/state/cart.query';
import { AkitaAuthQuery } from '../../auth/state/auth.query';
import { AkitaProductsQuery } from '../../products/state/products.query';
import { AkitaCheckoutQuery } from './checkout.query';
import { PaymentConfigurationModel } from '../models/payment-configuration.model';
import { PaymentConfigurationApiService } from '../services/payment-configurations.service';
import { AkitaPaymentService } from '../../payments/state/payment.service';
import { CheckoutStep } from '../models/checkout.state';
import { PaymentRequestInfo } from '../models/payment-request-info.model';
import { OrderApiService } from '../services/payment-order.service';
import { PaymentOrderModel } from '../models/payment-order.model';
import { PaymentModel } from '../models/payment.model';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import {
  logBeginCheckout,
  logChangedStcPhoneNumber,
  logCreatePayment,
  logStcErrorPayment,
} from '@app/core/services/google-analytics.lazy';
import { UserDetails } from '../../addresses/models/user-details.model';
import { PaymentMethod, PaymentProvider } from '../models/payment.provider.model';
import { PhoneMetadata } from '@app/components/inputs/phone-input/popsy-phone-input.component';
import { RiskJsActionsService } from '@app/checkout/payment-providers/checkout-com/services/risk.js.actions.service';
import { MSClarityService } from '@app/shared/services/msclarity.service';
import { QUARA_MIN_AMOUNT } from '@app/checkout/payment-providers/quara/components/confirm-quara-form/confirm-quara-form.component';
import { configureScope } from '@sentry/browser';

const SESSION_INFO_KEY = 'popsy_si';

@Injectable({ providedIn: 'root' })
export class AkitaCheckoutService {
  private readonly sessionApi = inject(SessionApiService);

  constructor(
    private readonly zone: NgZone,
    private readonly store: AkitaCheckoutStore,
    private readonly query: AkitaCheckoutQuery,
    private readonly akitaCartQuery: AkitaCartQuery,
    private readonly akitaAuthQuery: AkitaAuthQuery,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly paymentConfigurationApi: PaymentConfigurationApiService,
    private readonly akitaPaymentService: AkitaPaymentService,
    private readonly orderApi: OrderApiService,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly riskJsActionsService: RiskJsActionsService,
    private readonly msClarityService: MSClarityService
  ) {}

  public reset(): void {
    this.store.reset();
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('resetCheckoutState() Called');
        this.store.setErrorFetchingSession(null);
        this.store.toggleFetchingSession(true);
      });
    });
  }

  public createSessionAsync(): Subscription {
    return this.createSession().subscribe({
      next: () => {},
      error: () => {},
    });
  }

  public createSession(): Observable<SessionModel | null> {
    this.reset();

    this.zone.run(() => {
      applyTransaction(() => {
        logAction('createSession() Called');
        this.store.setErrorFetchingSession(null);
        this.store.toggleFetchingSession(true);
      });
    });
    const cartId = this.akitaCartQuery.getId();
    const userId = this.akitaAuthQuery.user?.id || null;
    const email = this.akitaAuthQuery.user?.email || null;
    const lang = this.akitaProductsQuery.validateLocale().language;

    return this.sessionApi.createSessionApi(cartId, userId, email, lang).pipe(
      catchError((error: unknown) => {
        const parsed = parseApiError(error);
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('createSession() - error');
            this.store.setErrorFetchingSession(parsed);
            this.store.toggleFetchingSession(false);
          });
        });
        return throwError(() => parsed);
      }),
      tap((response: SessionModel | null) => {
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('createSession - done');
            this.store.setSession(response);
            this.store.toggleFetchingSession(false);
            this.saveSessionInfo();

            this.googleAnalyticsService.setSessionUserDetailsIntoDataLayer();

            logBeginCheckout(
              this.googleAnalyticsService.logEventWrapper,
              this.googleAnalyticsService.firebaseRecordAddToCartADConversion,
              this.googleAnalyticsService.facebookTrackInitiateCheckout,
              response?.order || [],
              response?.totalPrice || null,
              'WEB',
              Boolean(!response?.customerId)
            );
          });
        });
      })
    );
  }

  public fetchSessionAsync(sessionId: string): Subscription {
    return this.fetchSession(sessionId).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  public fetchSession(sessionId: string): Observable<SessionModel | null> {
    if (this.query.fetchingSession) {
      return of(null);
    }
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('getSession() Called');
        this.store.setErrorFetchingSession(null);
        this.store.toggleFetchingSession(true);
      });
    });

    return this.sessionApi.getSessionApi(sessionId).pipe(
      catchError((error: unknown) => {
        const parsed = parseApiError(error);
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('getSession() - error');
            this.store.setErrorFetchingSession(parsed);
            this.store.toggleFetchingSession(false);
          });
        });
        return throwError(() => parsed);
      }),
      tap((response: SessionModel | null) => {
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('getSession - done');
            this.store.setSession(response);
            this.store.toggleFetchingSession(false);
            this.googleAnalyticsService.setSessionUserDetailsIntoDataLayer();
          });
        });
      })
    );
  }

  public updateSession(
    sessionId: string,
    userDetails: UserDetails | null,
    discountCode?: string | null,
    paymentProvider?: PaymentProvider | null,
    paymentMethod?: PaymentMethod | null,
    expressCheckout?: boolean | null
  ): Observable<SessionModel | null> {
    if (this.query.fetchingSession) {
      return of(null);
    }
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('updateSession() Called');
        this.store.setErrorFetchingSession(null);
        this.store.toggleFetchingSession(true);
      });
    });

    // set express checkout when changing payment provider or method
    // we use this to track if the user has used express checkout
    if (paymentMethod && paymentProvider) {
      this.zone.run(() => {
        applyTransaction(() => {
          logAction(`updateSession() Set expressCheckout ${expressCheckout || false}`);
          this.store.setIsExpressCheckoutOrder(expressCheckout || false);
        });
      });
    }

    // SESSION LOCKED
    if (this.query.session?.isLocked) {
      this.zone.run(() => {
        applyTransaction(() => {
          logAction('updateSession() Locally');
          this.store.toggleFetchingSession(false);
          if (paymentProvider && paymentMethod) {
            this.store.setPaymentProvider(paymentProvider);
            this.store.setPaymentMethod(paymentMethod);
          }
          if (discountCode) {
            this.store.setDiscountCode(
              this.akitaRouterQuery.getDiscountCodeInfo(
                discountCode,
                this.query.session?.storeCurrency
              )
            );
            this.store.setGotDiscountAfterLocked(true);
          }
          if (userDetails) {
            this.store.setSessionUserDetails(userDetails);
          }
        });
      });
      return of(this.query.session);
    } else {
      return this.sessionApi
        .updateSessionApi(
          sessionId,
          userDetails,
          discountCode,
          paymentProvider,
          paymentMethod
        )
        .pipe(
          catchError((error: unknown) => {
            const parsed = parseApiError(error);

            this.zone.run(() => {
              applyTransaction(() => {
                logAction('updateSession() - error');
                this.store.setErrorFetchingSession(parsed);
                this.store.toggleFetchingSession(false);
              });
            });
            return throwError(() => parsed);
          }),
          tap((response: SessionModel | null) => {
            this.zone.run(() => {
              applyTransaction(() => {
                logAction('updateSession - done');
                this.store.setSession(response);
                this.store.toggleFetchingSession(false);
                this.googleAnalyticsService.setSessionUserDetailsIntoDataLayer();
              });
            });
          })
        );
    }
  }

  // Update session discount code only
  // even if the session is locked
  public updateSessionDiscount(
    sessionId: string,
    discountCode?: string | null
  ): Observable<SessionModel | null> {
    if (this.query.fetchingSession) {
      return of(null);
    }
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('updateSessionDiscount() Called');
        this.store.setErrorFetchingSession(null);
        this.store.toggleFetchingSession(true);
      });
    });

    return this.sessionApi.updateSessionDiscountApi(sessionId, discountCode).pipe(
      catchError((error: unknown) => {
        const parsed = parseApiError(error);

        this.zone.run(() => {
          applyTransaction(() => {
            logAction('updateSession() - error');
            this.store.setErrorFetchingSession(parsed);
            this.store.toggleFetchingSession(false);
          });
        });
        return throwError(() => parsed);
      }),
      tap((response: SessionModel | null) => {
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('updateSession - done');
            this.store.setSession(response);
            this.store.toggleFetchingSession(false);
            this.googleAnalyticsService.setSessionUserDetailsIntoDataLayer();
          });
        });
      })
    );
  }

  public sendOtpVerification(
    paymentId: string,
    otp: string
  ): Observable<PaymentModel | null> {
    if (this.query.fetchingPayment) {
      return of(null);
    }

    this.zone.run(() => {
      applyTransaction(() => {
        logAction('sendOtpVerification() Called');
        this.store.setErrorFetchingPayment(null);
        this.store.toggleFetchingPayment(true);
      });
    });

    return this.orderApi.sendOtpVerificationApi(paymentId, otp).pipe(
      catchError((error: unknown) => {
        const parsed = parseApiError(error);
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('sendOtpVerification() - error');
            this.store.setErrorFetchingPayment(parsed);
            this.store.toggleFetchingPayment(false);
          });
        });
        return throwError(() => parsed);
      }),
      tap((response: any) => {
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('sendOtpVerification - done');
            // STC PAY error handling
            if (response?.paymentError?.code) {
              this.store.setErrorStcPayment(response.paymentError);
              this.msClarityService.setTag('debug-error', 'stc-payment-error');
              logStcErrorPayment(
                this.googleAnalyticsService.logEventWrapper,
                response.paymentError.code,
                response.paymentError.message
              );

              logAction('updatePayment() STC PAY - error');
              this.store.setErrorUpdatingPayment(response.paymentError);
              this.store.toggleFetchingPayment(false);
            }
            this.store.toggleFetchingPayment(false);
          });
        });
      })
    );
  }

  public fetchConfigurationsAsync(): Subscription {
    return this.fetchConfigurations().subscribe({
      next: () => {},
      error: () => {},
    });
  }

  public fetchConfigurations(): Observable<Array<PaymentConfigurationModel> | null> {
    if (this.query.fetchingConfigurations) {
      return of(null);
    }

    const country =
      this.query.session?.storeCountry ||
      this.akitaProductsQuery.validateLocale().country;

    this.zone.run(() => {
      applyTransaction(() => {
        logAction('fetchConfigurations() Called');
        this.store.setErrorFetchingConfigurations(null);
        this.store.toggleFetchingConfigurations(true);
      });
    });

    return this.paymentConfigurationApi.getPaymentConfigurationsApi(country).pipe(
      catchError((error: unknown) => {
        const parsed = parseApiError(error);
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('fetchConfigurations() - error');
            this.store.setErrorFetchingConfigurations(parsed);
            this.store.toggleFetchingConfigurations(false);
          });
        });
        return throwError(() => parsed);
      }),
      tap((response: Array<PaymentConfigurationModel> | null) => {
        this.updatePaymentsPublicTokens(response);

        if (
          // eslint-disable-next-line no-constant-condition
          response?.find((item) => item.paymentProvider === 'QUARA')?.isEnabled &&
          (this.query.session?.totalPrice?.amount || 0) >= QUARA_MIN_AMOUNT
        ) {
          this.store.setShowQuaraPromo(true);
        }

        if (
          // eslint-disable-next-line no-constant-condition
          response?.find(
            (item) =>
              item.paymentProvider === 'CHECKOUT' &&
              item.paymentMethods.includes('BASEETA_CARD')
          )?.isEnabled ||
          true
        ) {
          this.store.setShowBaseetaPromo(true);
        }

        this.zone.run(() => {
          applyTransaction(() => {
            logAction('fetchConfigurations - done');
            this.store.setConfigurations(response);
            this.store.toggleFetchingConfigurations(false);
          });
        });
      })
    );
  }

  public fetchRankedConfigurationsAsync(orderId: string): Subscription {
    return this.fetchRankedConfigurations(orderId).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  public fetchRankedConfigurations(
    orderId: string
  ): Observable<Array<PaymentConfigurationModel> | null> {
    if (this.query.fetchingRankedConfigurations || orderId === '') {
      return of(null);
    }

    this.zone.run(() => {
      applyTransaction(() => {
        logAction('fetchRankedConfigurations() Called');
        this.store.setErrorFetchingRankedConfigurations(null);
        this.store.toggleFetchingRankedConfigurations(true);
      });
    });

    return this.paymentConfigurationApi.getRankedPaymentConfigurationsApi(orderId).pipe(
      catchError((error: unknown) => {
        const parsed = parseApiError(error);
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('fetchRankedConfigurations() - error');
            this.store.setErrorFetchingRankedConfigurations(parsed);
            this.store.toggleFetchingRankedConfigurations(false);
          });
        });
        return throwError(() => parsed);
      }),
      tap((response: Array<PaymentConfigurationModel> | null) => {
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('fetchRankedConfigurations - done');
            this.store.setRankedConfigurations(response);
            this.store.toggleFetchingRankedConfigurations(false);
          });
        });
      })
    );
  }

  public updatePaymentsPublicTokens(
    configs: Array<PaymentConfigurationModel> | null
  ): void {
    if (configs) {
      configs.forEach((config) => {
        if (config) {
          if (config.paymentProvider === 'TAMARA') {
            this.akitaPaymentService.updateTamaraPublicKey(config.publicToken);
          }
          if (config.paymentProvider === 'TABBY') {
            this.akitaPaymentService.updateTabbyPublicKey(config.publicToken);
          }
          if (config.paymentProvider === 'CHECKOUT') {
            this.akitaPaymentService.updateCheckoutDotComPublicKey(config.publicToken);
          }
        }
      });
    }
  }

  public fetchUserOrdersAsync(): Subscription {
    return this.fetchUserOrders().subscribe({
      next: () => {},
      error: () => {},
    });
  }

  public fetchUserOrders(): Observable<Array<PaymentOrderModel> | null> {
    if (this.query.fetchingOrder) {
      return of(null);
    }

    this.zone.run(() => {
      applyTransaction(() => {
        logAction('fetchUserOrders() Called');
        this.store.setErrorFetchingOrder(null);
        this.store.toggleFetchingOrder(true);
      });
    });

    return this.orderApi.getUserOrdersApi().pipe(
      catchError((error: unknown) => {
        const parsed = parseApiError(error);
        console.log('fetchUserOrders() - error', parsed, error);
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('fetchUserOrders() - error');
            this.store.setErrorFetchingOrder(parsed);
            this.store.toggleFetchingOrder(false);
          });
        });
        return throwError(() => parsed);
      }),
      tap((response: Array<PaymentOrderModel> | null) => {
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('fetchUserOrders - done');
            this.store.setUserOrders(response);
            this.store.toggleFetchingOrder(false);
          });
        });
      })
    );
  }

  public updateCheckoutStep(step: CheckoutStep): void {
    this.store.update({
      step: step,
    });
  }

  public updateSessionPhoneNumber(phoneNumber: PhoneMetadata | null) {
    const session = this.query.session;
    if (!session) {
      return;
    }
    session.phone = phoneNumber;
    this.zone.run(() => {
      logChangedStcPhoneNumber(
        this.googleAnalyticsService.logEventWrapper,
        !session.customerId
      );
      applyTransaction(() => {
        logAction('updateSessionPhoneNumber() Called');
        this.store.setSession(session);
        this.googleAnalyticsService.setSessionUserDetailsIntoDataLayer();
      });
    });
  }

  public setPaymentProvider(paymentProvider: PaymentProvider | null): void {
    this.store.update({
      paymentProvider: paymentProvider,
    });
  }

  public setPaymentMethod(paymentMethod: PaymentMethod | null): void {
    this.store.update({
      paymentMethod: paymentMethod,
    });
  }

  public setCKOPaymentToken(id: string | null): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setCKOPaymentToken()');
        this.store.setCKOPaymentToken(id);
      });
    });
  }

  public setCKOCardHolderName(name: string | null): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setCKOCardHolderName()');
        this.store.setCKOCardHolderName(name);
      });
    });
  }

  public setShowTabbyPromo(show: boolean | null): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setShowTabbyPromo()');
        this.store.setShowTabbyPromo(show);
      });
    });
  }

  public setShowWataniyaPromo(show: boolean | null): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setShowWataniyaPromo()');
        this.store.setShowWataniyaPromo(show);
      });
    });
  }

  public setShowQuaraPromo(show: boolean | null): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setShowQuaraPromo()');
        this.store.setShowQuaraPromo(show);
      });
    });
  }

  public setShowBaseetaPromo(show: boolean | null): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setShowBaseetaPromo()');
        this.store.setShowBaseetaPromo(show);
      });
    });
  }

  public setShowTamaraPromo(show: boolean | null): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setShowTamaraPromo()');
        this.store.setShowTamaraPromo(show);
      });
    });
  }

  public setShowApplePay(show: boolean | null): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setShowApplePay()');
        this.store.setShowApplePay(show);
      });
    });
  }

  public setShowGooglePay(show: boolean | null): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setShowGooglePay()');
        this.store.setShowGooglePay(show);
      });
    });
  }

  public setIsSubmitting(isSubmitting: boolean): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setIsSubmitting()');
        this.store.setIsSubmitting(isSubmitting);
      });
    });
  }

  public setOrderId(id: string | null): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setOrderId()');
        this.store.setOrderId(id);
      });
    });
  }

  public setPaymentId(id: string | null): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setPaymentId()');
        this.store.setPaymentId(id);
      });
    });
  }

  // deprecated
  // Use createPayment() instead
  public createOrder(): Observable<PaymentOrderModel | null> {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('createOrder() Called');
        this.store.setErrorFetchingOrder(null);
        this.store.toggleFetchingOrder(true);
      });
    });

    const queryParamsToKeep = this.akitaRouterQuery.queryParamsToKeep;
    const queryParamsList: Array<string> = [];
    for (const paramName of Object.keys(queryParamsToKeep)) {
      queryParamsList.push(`${paramName}=${queryParamsToKeep[paramName]}`);
    }

    this.zone.run(() => {
      applyTransaction(() => {
        logCreatePayment(
          this.googleAnalyticsService.logEventWrapper,
          this.googleAnalyticsService.facebookTrackAddPaymentInfo,
          this.query.session?.order.map((item) => item.productId || '') || '',
          this.query.session?.totalPrice?.amount || 0,
          this.query.session?.storeCurrency || 'SAR',
          this.query.session?.paymentProvider || null,
          this.query.session?.paymentMethod || null,
          !this.query.session?.customerId,
          !this.query.session?.isLocked
        );
      });
    });

    const info = this.query.generatePaymentDetails(queryParamsList.join('&'));

    if (this.query.session?.isLocked && this.query.session.orderId) {
      return this.updatePayment(this.query.session.orderId, info).pipe(
        switchMap((payment: PaymentModel | null) =>
          this.fetchOrder(payment?.orderId || null)
        )
      );
    } else {
      return this.orderApi.createOrderApi(info).pipe(
        catchError((error: unknown) => {
          const parsed = parseApiError(error);

          this.zone.run(() => {
            applyTransaction(() => {
              logAction('createOrder() - error');
              this.store.setErrorFetchingOrder(parsed);
              this.store.toggleFetchingOrder(false);
            });
          });
          return throwError(() => parsed);
        }),
        tap((response: PaymentOrderModel | null) => {
          this.zone.run(() => {
            applyTransaction(() => {
              logAction('createOrder - done');
              this.fetchSessionAsync(this.query.sessionId || '');
              this.store.setOrder(response);
              this.store.toggleFetchingOrder(false);
              this.saveSessionInfo();
            });
          });
        })
      );
    }
  }

  public createPayment(): Observable<PaymentModel | null> {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('createPayment() Called');
        this.store.setErrorFetchingOrder(null);
        this.store.toggleFetchingOrder(true);
      });
    });

    const queryParamsToKeep = this.akitaRouterQuery.queryParamsToKeep;
    const queryParamsList: Array<string> = [];
    for (const paramName of Object.keys(queryParamsToKeep)) {
      queryParamsList.push(`${paramName}=${queryParamsToKeep[paramName]}`);
    }

    this.zone.run(() => {
      applyTransaction(() => {
        logCreatePayment(
          this.googleAnalyticsService.logEventWrapper,
          this.googleAnalyticsService.facebookTrackAddPaymentInfo,
          this.query.session?.order.map((item) => item.productId || '') || '',
          this.query.session?.totalPrice?.amount || 0,
          this.query.session?.storeCurrency || 'SAR',
          this.query.session?.paymentProvider || null,
          this.query.session?.paymentMethod || null,
          !this.query.session?.customerId,
          !this.query.session?.isLocked,
          this.query.getValue().isExpressCheckoutOrder
        );
      });
    });

    // debug some tags on sentry
    const info = this.query.generatePaymentDetails(queryParamsList.join('&'));
    configureScope((scope) => {
      scope.setTag('payment:succes_url', `${info?.post_action_links?.on_success || '?'}`);
      if (queryParamsToKeep.utm_source) {
        scope.setTag('utm_campaign', `${queryParamsToKeep.utm_source || '?'}`);
      }
    });

    // locked session
    if (this.query.session?.isLocked && this.query.session.orderId) {
      return this.updatePayment(this.query.session.orderId, info).pipe(
        tap(() => {
          this.store.toggleFetchingOrder(false);
        })
      );
    } else {
      // not locked session
      return this.orderApi.createOrderApi(info).pipe(
        catchError((error: unknown) => {
          const parsed = parseApiError(error);

          this.zone.run(() => {
            applyTransaction(() => {
              logAction('createPayment() - error');
              this.store.setErrorFetchingOrder(parsed);
              this.store.toggleFetchingOrder(false);
            });
          });
          return throwError(() => parsed);
        }),
        tap((response: PaymentOrderModel | null) => {
          this.zone.run(() => {
            applyTransaction(() => {
              logAction('createPayment - done');
              this.fetchSessionAsync(this.query.sessionId || '');
              this.store.setOrder(response);
              this.store.toggleFetchingOrder(false);
              this.saveSessionInfo();

              // STC PAY error handling
              if (
                response?.paymentError?.code &&
                this.query.session?.paymentMethod === 'STC_PAY'
              ) {
                this.store.setErrorStcPayment(response.paymentError);
                this.msClarityService.setTag('debug-error', 'stc-payment-error');
                logStcErrorPayment(
                  this.googleAnalyticsService.logEventWrapper,
                  response.paymentError.code,
                  response.paymentError.message
                );
                this.zone.run(() => {
                  applyTransaction(() => {
                    logAction('createPayment() - error');
                    this.store.setErrorFetchingOrder(response.paymentError);
                    this.store.toggleFetchingOrder(false);
                  });
                });
              }
            });
          });
        }),
        switchMap((order: PaymentOrderModel | null) =>
          this.fetchPayment(order?.id || null).pipe(
            catchError((error: unknown) => {
              const parsed = parseApiError(error);

              this.zone.run(() => {
                applyTransaction(() => {
                  logAction('createPayment() - error');
                  this.store.setErrorFetchingOrder(parsed);
                  this.store.toggleFetchingOrder(false);
                });
              });
              return throwError(() => parsed);
            })
          )
        )
      );
    }
  }

  public fetchOrder(orderId: string | null): Observable<PaymentOrderModel | null> {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('getOrder() Called');
        this.store.setErrorFetchingOrder(null);
        this.store.toggleFetchingOrder(true);
      });
    });

    return this.orderApi.getOrderApi(orderId).pipe(
      catchError((error: unknown) => {
        const parsed = parseApiError(error);
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('getOrder() - error');
            this.store.setErrorFetchingOrder(parsed);
            this.store.toggleFetchingOrder(false);
          });
        });
        return throwError(() => parsed);
      }),
      tap((response: PaymentOrderModel | null) => {
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('getOrder - done');
            this.store.setOrder(response);
            this.store.toggleFetchingOrder(false);
          });
        });
      })
    );
  }

  public fetchPayment(orderId: string | null) {
    if (this.query.getValue().fetchingPayment || this.query.getValue().fetchingOrder) {
      return of(null);
    }

    this.zone.run(() => {
      applyTransaction(() => {
        logAction('getPayment() Called');
        this.store.setErrorFetchingPayment(null);
        this.store.toggleFetchingPayment(true);
      });
    });

    return this.orderApi.getPaymentApi(orderId).pipe(
      catchError((error: unknown) => {
        const parsed = parseApiError(error);
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('getPayment() - error');
            this.store.setErrorFetchingPayment(parsed);
            this.store.toggleFetchingPayment(false);
          });
        });
        return throwError(() => parsed);
      }),
      tap((response: PaymentModel | null) => {
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('getPayment - done');
            this.store.setPayment(response);
            this.store.toggleFetchingPayment(false);
          });
        });
      })
    );
  }

  public updatePayment(
    orderId: string,
    info: PaymentRequestInfo | null
  ): Observable<PaymentModel | null> {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('updatePayment() Called');
        this.store.setErrorUpdatingPayment(null);
        this.store.toggleUpdatingPayment(true);
      });
    });

    return this.orderApi
      .updatePaymentApi(
        orderId,
        info,
        this.query.session?.paymentProvider,
        this.query.session?.paymentMethod,
        this.query.session?.discount?.code
      )
      .pipe(
        catchError((error: unknown) => {
          const parsed = parseApiError(error);
          this.zone.run(() => {
            applyTransaction(() => {
              logAction('updatePayment() - error');
              this.store.setErrorUpdatingPayment(parsed);
              this.store.toggleUpdatingPayment(false);
            });
          });
          return throwError(() => parsed);
        }),
        tap((response: PaymentModel | null) => {
          this.zone.run(() => {
            applyTransaction(() => {
              logAction('updatePayment - done');
              this.store.setPayment(response);
              this.store.toggleUpdatingPayment(false);

              // STC PAY error handling
              if (response?.paymentError?.code && response.paymentMethod === 'STC_PAY') {
                this.store.setErrorStcPayment(response.paymentError);
                this.msClarityService.setTag('debug-error', 'stc-payment-error');
                logStcErrorPayment(
                  this.googleAnalyticsService.logEventWrapper,
                  response.paymentError.code,
                  response.paymentError.message
                );
                this.zone.run(() => {
                  applyTransaction(() => {
                    logAction('updatePayment() STC PAY - error');
                    this.store.setErrorUpdatingPayment(response.paymentError);
                    this.store.toggleUpdatingPayment(false);
                  });
                });
              }
            });
          });
        })
      );
  }

  public saveSessionInfo(): Subscription {
    const out = new Subscription();

    this.zone.runOutsideAngular(() => {
      if (this.akitaRouterQuery.isBrowser) {
        try {
          if (typeof window !== 'undefined' && 'localStorage' in window) {
            window.localStorage.setItem(
              SESSION_INFO_KEY,
              JSON.stringify({
                sessionId: this.query.session?.id,
                cartId: this.query.session?.cartId,
                orderId: this.query.order?.id,
              })
            );
          }
        } catch (error) {}
      }
    });

    return out;
  }

  public clearSessionInfo(): Subscription {
    const out = new Subscription();

    this.zone.runOutsideAngular(() => {
      if (this.akitaRouterQuery.isBrowser) {
        if (window && window.localStorage && window.localStorage.removeItem) {
          try {
            window.localStorage.setItem(SESSION_INFO_KEY, '');
            window.localStorage.removeItem(SESSION_INFO_KEY);
          } catch (error) {}
        }
      }
    });

    return out;
  }

  public initializeRiskJs(): Subscription {
    const ckoPublicToken = this.query
      .getValue()
      ?.configurations?.find(
        (config) => config.paymentProvider === 'CHECKOUT'
      )?.publicToken;

    this.zone.run(() => {
      applyTransaction(() => {
        logAction('initializeRiskJs() Called');
      });
    });

    if (ckoPublicToken && this.query.getValue().isRiskJsInitialized === false) {
      return this.riskJsActionsService.initialize(ckoPublicToken).subscribe({
        next: () => {
          this.zone.run(() => {
            applyTransaction(() => {
              logAction('initializeRiskJs - done');
              this.store.setIsRiskJsInitialized(true);
            });
          });
        },
        error: () => {
          this.zone.run(() => {
            applyTransaction(() => {
              logAction('initializeRiskJs - error');
              this.store.setIsRiskJsInitialized(false);
            });
          });
        },
      });
    } else {
      return new Subscription();
    }
  }

  public generateRiskJsDeviceId(): Observable<string | null> {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('generateRiskJsDeviceId() Called');
      });
    });

    if (this.query.getValue().isRiskJsDataPublished) {
      return of(this.query.getValue().riskJsDeviceSessionId);
    }

    return this.riskJsActionsService.publishRiskData().pipe(
      catchError((error: unknown) => {
        const parsed = parseApiError(error);
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('generateRiskJsDeviceId() - error');
          });
        });

        return throwError(() => parsed);
      }),
      tap((response: string | null) => {
        if (response) {
          this.zone.run(() => {
            applyTransaction(() => {
              logAction('initializeRiskJs() - Done');
              this.store.setRiskJsDeviceSessionId(response);
              this.store.setIsRiskJsDataPublished(true);
            });
          });
        }
      })
    );
  }

  public setErrorStcPayment(error: ApiError | null): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setErrorStcPayment - error');
        this.store.setErrorStcPayment(error);
      });
    });
  }

  public placeOrder(isPlacingOrder: boolean): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('placeOrder() Called');
        this.store.toggleIsPlacingOrder(isPlacingOrder);
      });
    });

    setTimeout(() => {
      if (this.query.getValue().isPlacingOrder) {
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('placeOrder() Timeout');
            this.store.toggleIsPlacingOrder(false);
          });
        });
      }
    }, 1000);
  }

  public setIsExpressCheckout(isExpressCheckout: boolean): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setIsExpressCheckout()');
        this.store.setIsExpressCheckoutOrder(isExpressCheckout);
      });
    });
  }

  public setIsRetryPaymentPopupOpen(isRetryPaymentPopupOpen: boolean): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setIsRetryPaymentPopupOpen()');
        this.store.setIsRetryPaymentPopupOpen(isRetryPaymentPopupOpen);
      });
    });
  }
}
